import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, IconButton, Switch, Tooltip, Dialog, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { useIntl } from 'react-intl'

import { TableUrlPagination, ThemeButton } from '../../../../ui'
import { toggleInstallerActive, deleteInstaller } from '../../slices'
import { PrescriptorInstallersDialog } from '../dialog'
import { INSTALLERS_TABLE_COLUMNS, INSTALLERS_TABLE_ACTIONS } from '../../constants'

const getLookup = (combo) => {
  const lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const PrescriptorInstallersTable = () => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const combos = useSelector((state) => state.combos)
  const { data, search, pagination, isLoading } = useSelector(
    (state) => state.prescriptorInstallers
  )
  const prescriptorId = useSelector((state) => state.global.empresa_id)
  const totalEntries = pagination?.total_instaladores || 0

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedInstaller, setSelectedInstaller] = useState(null)

  const handleEdit = (installer) => {
    setSelectedInstaller(installer)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedInstaller(null)
  }

  const handleToggleActive = (installer) => {
    const isValidated =
      installer.validado && installer.validado !== 'false' && installer.validado !== 0

    if (!isValidated) {
      return
    }
    dispatch(toggleInstallerActive(installer.id, installer.activo, prescriptorId))
  }

  const formattedColumns = useMemo(
    () =>
      [...INSTALLERS_TABLE_COLUMNS, ...INSTALLERS_TABLE_ACTIONS].map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          size: column.width || 200,
          minSize: column.minWidth || 100,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: { align: column.align || 'left' },
          muiTableBodyCellProps: { align: column.align || 'left' },
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            header: 'Acciones',
            muiTableHeadCellProps: { align: 'center' },
            muiTableBodyCellProps: { align: 'center' },
            Cell: ({ row }) => {
              const isValidated =
                row.original.validado &&
                row.original.validado !== 'false' &&
                row.original.validado !== 0

              return (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(row.original)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      !isValidated
                        ? 'El instalador debe estar validado para activar'
                        : row.original.activo
                          ? 'Desactivar'
                          : 'Activar'
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={!!row.original.activo}
                        onChange={() => handleToggleActive(row.original)}
                        disabled={!isValidated}
                      />
                    </span>
                  </Tooltip>
                </Box>
              )
            },
          }
        }

        const result = {
          ...baseColumn,
          header: (() => {
            try {
              return formatMessage({
                id: `pages.prescriptor.installers.table.columns.${column.field}.title`,
              })
            } catch (e) {
              // Fallback si la traducción no existe
              const fieldNames = {
                nombre: 'Razón Social',
                cif: 'NIF/CIF',
                email: 'Email',
                telefono: 'Teléfono',
                poblacion: 'Población',
                empresa_ca_id: 'Comunidad Autónoma',
                validado: 'Validado',
              }
              return fieldNames[column.field] || column.field
            }
          })(),
        }

        if (column.type === 'boolean') {
          result.Cell = ({ cell }) => {
            const val = cell.getValue()
            return <span>{val ? 'Sí' : 'No'}</span>
          }
        } else if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          result.Cell = ({ cell, row }) => {
            let val = cell.getValue()
            if (column.field === 'empresa_ca_id' && row.original.comunidad_autonoma) {
              val = row.original.comunidad_autonoma
            }
            return <span>{lookup[val] || val || '--'}</span>
          }
        } else {
          result.Cell = ({ cell }) => {
            const val = cell.getValue()
            return <span>{val === null || val === undefined || val === '' ? '--' : val}</span>
          }
        }

        return result
      }),
    [combos, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
      muiTableProps: { sx: { tableLayout: 'fixed' } },
    }),
    []
  )

  return (
    <>
      <TableUrlPagination
        columns={formattedColumns}
        options={tableOptions}
        totalEntries={totalEntries}
        search={search}
        data={data}
        isLoading={isLoading}
      />
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <PrescriptorInstallersDialog installer={selectedInstaller} onClose={handleCloseDialog} />
      </Dialog>
    </>
  )
}
